/**
 * Which renderer and which GPU the browser actually picked.
 *
 * ## ⚠️ Why this exists
 *
 * The same build looked **fine on an M4 MacBook** and **wrong on two Windows
 * machines** — one with a dedicated RTX 3050, one a VM with no GPU at all
 * (2026-08-07). A real GPU and a software rasteriser producing the *same*
 * artifact is the tell: the problem is in what we ask for, not in how it gets
 * drawn. That points at the backend rather than the hardware, and our client is
 * `WebGPURenderer` **with a WebGL2 fallback** — so the two platforms may not be
 * running the same code at all.
 *
 * ⚠️ **The brief already said to do this**, §8: *"have the game log the
 * detected GPU on startup so we can see which one it picked. Check this before
 * diagnosing any performance problem."* Two hours of reasoning went into that
 * question before anybody read it.
 *
 * three exposes neither the WebGPU adapter nor the WebGL renderer string, so
 * both are fetched here directly.
 */

/** What the browser is really doing, in one line fit to read out loud. */
export interface RendererInfo {
  /** `'WebGPU'` or `'WebGL2'` — the thing that actually differs between hosts. */
  readonly backend: string;
  /** The GPU as the driver names it, or a note saying it would not say. */
  readonly gpu: string;
}

/**
 * ⚠️ Defensive about the adapter-info shape on purpose.
 *
 * `GPUAdapter.info` is a property in current Chromium and was an async
 * `requestAdapterInfo()` before that, and some builds report every field as an
 * empty string for privacy. All three are handled, because the point of this
 * function is to *answer a question about an unknown browser* — throwing on one
 * of them would defeat it.
 */
async function webgpuAdapter(): Promise<string> {
  try {
    const gpu = (navigator as Navigator & { gpu?: { requestAdapter(): Promise<unknown> } }).gpu;
    if (!gpu) return 'no navigator.gpu';

    const adapter = (await gpu.requestAdapter()) as
      | {
          info?: Record<string, string>;
          requestAdapterInfo?: () => Promise<Record<string, string>>;
        }
      | null
      | undefined;
    if (!adapter) return 'no adapter';

    const info =
      adapter.info ?? (adapter.requestAdapterInfo ? await adapter.requestAdapterInfo() : undefined);
    if (!info) return 'adapter would not say';

    const parts = [info['vendor'], info['architecture'], info['device'], info['description']]
      .filter((v) => typeof v === 'string' && v !== '')
      .join(' · ');
    return parts === '' ? 'adapter reported nothing' : parts;
  } catch {
    return 'adapter query failed';
  }
}

/**
 * The WebGL renderer string, via the debug extension.
 *
 * ⚠️ A **throwaway** context, not the renderer's own. Reaching into a live
 * context to read a parameter is the kind of thing that works until it does
 * not, and this runs once at startup where the cost is irrelevant.
 */
function webglRenderer(): string {
  try {
    const canvas = document.createElement('canvas');
    const gl = canvas.getContext('webgl2');
    if (!gl) return 'no webgl2 context';
    const ext = gl.getExtension('WEBGL_debug_renderer_info');
    const name = ext
      ? String(gl.getParameter(ext.UNMASKED_RENDERER_WEBGL))
      : String(gl.getParameter(gl.RENDERER));
    /* Free it rather than leaving a second GL context alive for the session. */
    gl.getExtension('WEBGL_lose_context')?.loseContext();
    return name === '' ? 'driver would not say' : name;
  } catch {
    return 'renderer query failed';
  }
}

/**
 * Ask the browser what it is using.
 *
 * @param isWebGPU whether three ended up on its WebGPU backend — read from the
 *   renderer rather than guessed from feature detection, because what matters
 *   is the path three *took*, not the one the browser could have offered.
 */
export async function rendererInfo(isWebGPU: boolean): Promise<RendererInfo> {
  return {
    backend: isWebGPU ? 'WebGPU' : 'WebGL2',
    gpu: isWebGPU ? await webgpuAdapter() : webglRenderer(),
  };
}
